import { FACE_SHAPES } from '../data/shapeContent';
import type { FaceAnalysis, Point2D } from '../types';
import type { PreparedPhoto } from './imageUtils';

const PAPER = '#f3efe6';
const SURFACE = '#fffdf8';
const INK = '#171512';
const MUTED = '#686056';
const RULE = '#c9c0b3';
const ACCENT = '#df4d2e';

function wrapText(
  context: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
  maxLines = Number.POSITIVE_INFINITY,
): number {
  const words = text.split(/\s+/);
  let line = '';
  let lineCount = 0;
  let currentY = y;

  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (context.measureText(candidate).width <= maxWidth || line === '') {
      line = candidate;
      continue;
    }

    context.fillText(line, x, currentY);
    lineCount += 1;
    if (lineCount >= maxLines) return currentY + lineHeight;
    currentY += lineHeight;
    line = word;
  }

  if (line && lineCount < maxLines) {
    context.fillText(line, x, currentY);
    currentY += lineHeight;
  }

  return currentY;
}

function drawEndpoint(context: CanvasRenderingContext2D, x: number, y: number): void {
  context.fillRect(x - 5, y - 5, 10, 10);
}

function drawPhotoStage(
  context: CanvasRenderingContext2D,
  photo: PreparedPhoto,
  analysis: FaceAnalysis,
): void {
  const frame = { x: 650, y: 205, width: 470, height: 600 };
  context.fillStyle = SURFACE;
  context.fillRect(frame.x, frame.y, frame.width, frame.height);
  context.strokeStyle = INK;
  context.lineWidth = 3;
  context.strokeRect(frame.x, frame.y, frame.width, frame.height);

  const scale = Math.min(frame.width / photo.width, frame.height / photo.height);
  const drawWidth = photo.width * scale;
  const drawHeight = photo.height * scale;
  const drawX = frame.x + (frame.width - drawWidth) / 2;
  const drawY = frame.y + (frame.height - drawHeight) / 2;
  context.drawImage(photo.analysisCanvas, drawX, drawY, drawWidth, drawHeight);

  const mapPoint = (point: Point2D): Point2D => ({
    x: drawX + point.x * drawWidth,
    y: drawY + point.y * drawHeight,
  });

  if (analysis.oval.length > 2) {
    context.beginPath();
    analysis.oval.forEach((point, index) => {
      const mapped = mapPoint(point);
      if (index === 0) context.moveTo(mapped.x, mapped.y);
      else context.lineTo(mapped.x, mapped.y);
    });
    context.closePath();
    context.strokeStyle = ACCENT;
    context.lineWidth = 4;
    context.stroke();
  }

  context.strokeStyle = ACCENT;
  context.fillStyle = ACCENT;
  context.lineWidth = 3;
  for (const segment of analysis.segments) {
    const start = mapPoint(segment.start);
    const end = mapPoint(segment.end);
    context.beginPath();
    context.moveTo(start.x, start.y);
    context.lineTo(end.x, end.y);
    context.stroke();
    drawEndpoint(context, start.x, start.y);
    drawEndpoint(context, end.x, end.y);
  }
}

function drawManualStage(context: CanvasRenderingContext2D, analysis: FaceAnalysis): void {
  const frame = { x: 650, y: 205, width: 470, height: 600 };
  context.fillStyle = SURFACE;
  context.fillRect(frame.x, frame.y, frame.width, frame.height);
  context.strokeStyle = INK;
  context.lineWidth = 3;
  context.strokeRect(frame.x, frame.y, frame.width, frame.height);

  const centerX = frame.x + frame.width / 2;
  const topY = frame.y + 70;
  const bottomY = frame.y + frame.height - 65;
  const maxWidth = 270;
  const metrics = analysis.metrics;
  const levels = [
    { y: topY + 95, width: maxWidth * metrics.foreheadRatio },
    { y: topY + 210, width: maxWidth * metrics.cheekRatio },
    { y: topY + 350, width: maxWidth * metrics.jawRatio },
  ];

  context.beginPath();
  context.moveTo(centerX, topY);
  context.bezierCurveTo(
    centerX + levels[0]!.width * 0.62,
    topY + 30,
    centerX + levels[1]!.width * 0.62,
    topY + 150,
    centerX + levels[1]!.width / 2,
    levels[1]!.y,
  );
  context.bezierCurveTo(
    centerX + levels[2]!.width * 0.62,
    topY + 310,
    centerX + maxWidth * metrics.lowerJawRatio * 0.4,
    bottomY - 70,
    centerX,
    bottomY,
  );
  context.bezierCurveTo(
    centerX - maxWidth * metrics.lowerJawRatio * 0.4,
    bottomY - 70,
    centerX - levels[2]!.width * 0.62,
    topY + 310,
    centerX - levels[1]!.width / 2,
    levels[1]!.y,
  );
  context.bezierCurveTo(
    centerX - levels[1]!.width * 0.62,
    topY + 150,
    centerX - levels[0]!.width * 0.62,
    topY + 30,
    centerX,
    topY,
  );
  context.closePath();
  context.strokeStyle = INK;
  context.lineWidth = 6;
  context.stroke();

  context.strokeStyle = ACCENT;
  context.fillStyle = ACCENT;
  context.lineWidth = 3;
  context.beginPath();
  context.moveTo(centerX, topY);
  context.lineTo(centerX, bottomY);
  context.stroke();
  drawEndpoint(context, centerX, topY);
  drawEndpoint(context, centerX, bottomY);

  for (const level of levels) {
    const startX = centerX - level.width / 2;
    const endX = centerX + level.width / 2;
    context.beginPath();
    context.moveTo(startX, level.y);
    context.lineTo(endX, level.y);
    context.stroke();
    drawEndpoint(context, startX, level.y);
    drawEndpoint(context, endX, level.y);
  }
}

export function buildAnalysisSummary(analysis: FaceAnalysis): string {
  const primary = FACE_SHAPES[analysis.primary.id];
  const alternatives = analysis.matches
    .slice(1, 3)
    .map((match) => FACE_SHAPES[match.id].label)
    .join(', ');

  return [
    `My closest face-shape match is ${primary.label}.`,
    analysis.explanation,
    `Close alternatives: ${alternatives}.`,
    `Face length / width: ${analysis.metrics.lengthToWidth.toFixed(2)}:1.`,
    `Forehead / jaw: ${analysis.metrics.foreheadToJaw.toFixed(2)}:1.`,
    'Generated privately in my browser at whatsmyfaceshape.org. Face-shape categories are styling guides, not medical or identity determinations.',
  ].join('\n');
}

export async function copyAnalysisSummary(analysis: FaceAnalysis): Promise<void> {
  await navigator.clipboard.writeText(buildAnalysisSummary(analysis));
}

export async function downloadAnalysisReport(
  analysis: FaceAnalysis,
  photo: PreparedPhoto | null,
): Promise<void> {
  const canvas = document.createElement('canvas');
  canvas.width = 1200;
  canvas.height = 1380;
  const context = canvas.getContext('2d', { alpha: false });
  if (!context) throw new Error('Report export is unavailable in this browser.');

  context.fillStyle = PAPER;
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.fillStyle = ACCENT;
  context.font = '700 21px Arial, sans-serif';
  context.fillText('PRIVATE • ON-DEVICE • EXPLAINABLE', 70, 76);

  context.fillStyle = INK;
  context.font = '700 69px Georgia, serif';
  context.fillText("What's my face shape?", 70, 155);
  context.strokeStyle = RULE;
  context.lineWidth = 2;
  context.beginPath();
  context.moveTo(70, 180);
  context.lineTo(1130, 180);
  context.stroke();

  const primary = FACE_SHAPES[analysis.primary.id];
  context.font = '700 24px Arial, sans-serif';
  context.fillStyle = MUTED;
  context.fillText('CLOSEST GEOMETRY MATCH', 70, 238);
  context.font = '700 105px Georgia, serif';
  context.fillStyle = INK;
  context.fillText(primary.label, 70, 350);
  context.font = '700 22px Arial, sans-serif';
  context.fillStyle = ACCENT;
  context.fillText(
    analysis.character === 'blended' ? 'BLENDED RESULT • CLOSE SECOND PROFILE' : 'LEADING GEOMETRY PROFILE',
    76,
    396,
  );

  context.font = '400 28px Arial, sans-serif';
  context.fillStyle = INK;
  const explanationBottom = wrapText(context, analysis.explanation, 70, 456, 510, 40, 5);

  context.font = '700 21px Arial, sans-serif';
  context.fillStyle = MUTED;
  context.fillText('WHY THIS MATCHED', 70, explanationBottom + 32);
  context.font = '400 24px Arial, sans-serif';
  context.fillStyle = INK;
  let reasonY = explanationBottom + 76;
  for (const reason of analysis.reasons.slice(0, 3)) {
    context.fillStyle = ACCENT;
    context.fillRect(72, reasonY - 15, 9, 9);
    context.fillStyle = INK;
    reasonY = wrapText(context, reason, 98, reasonY, 480, 34, 2) + 10;
  }

  if (photo) drawPhotoStage(context, photo, analysis);
  else drawManualStage(context, analysis);

  context.strokeStyle = RULE;
  context.beginPath();
  context.moveTo(70, 850);
  context.lineTo(1130, 850);
  context.stroke();

  context.fillStyle = MUTED;
  context.font = '700 20px Arial, sans-serif';
  context.fillText('MEASURED PROPORTIONS', 70, 902);

  const metricRows = [
    ['Face length / width', analysis.metrics.lengthToWidth.toFixed(2)],
    ['Forehead / jaw', analysis.metrics.foreheadToJaw.toFixed(2)],
    ['Cheek prominence', analysis.metrics.cheekProminence.toFixed(2)],
    ['Jaw corner angle', `${Math.round(analysis.metrics.jawAngle)}°`],
  ];

  context.font = '400 27px Arial, sans-serif';
  metricRows.forEach(([label, value], index) => {
    const y = 955 + index * 60;
    context.fillStyle = MUTED;
    context.fillText(label ?? '', 70, y);
    context.fillStyle = INK;
    context.font = '700 28px Arial, sans-serif';
    context.textAlign = 'right';
    context.fillText(value ?? '', 555, y);
    context.textAlign = 'left';
    context.font = '400 27px Arial, sans-serif';
    context.strokeStyle = RULE;
    context.beginPath();
    context.moveTo(70, y + 18);
    context.lineTo(575, y + 18);
    context.stroke();
  });

  context.fillStyle = MUTED;
  context.font = '700 20px Arial, sans-serif';
  context.fillText('CLOSE ALTERNATIVES', 650, 902);
  context.font = '700 32px Georgia, serif';
  analysis.matches.slice(1, 4).forEach((match, index) => {
    const y = 962 + index * 76;
    context.fillStyle = INK;
    context.fillText(FACE_SHAPES[match.id].label, 650, y);
    context.font = '700 20px Arial, sans-serif';
    context.fillStyle = ACCENT;
    context.fillText(index === 0 ? 'VERY CLOSE' : index === 1 ? 'POSSIBLE' : 'ALTERNATIVE', 1010, y);
    context.font = '700 32px Georgia, serif';
  });

  context.strokeStyle = INK;
  context.lineWidth = 3;
  context.beginPath();
  context.moveTo(70, 1245);
  context.lineTo(1130, 1245);
  context.stroke();
  context.fillStyle = INK;
  context.font = '700 24px Arial, sans-serif';
  context.fillText('whatsmyfaceshape.org', 70, 1300);
  context.fillStyle = MUTED;
  context.font = '400 18px Arial, sans-serif';
  context.fillText('Your photo stayed in this browser. No account or upload was used.', 70, 1336);
  context.textAlign = 'right';
  context.fillText('A styling guide, not a medical or identity determination.', 1130, 1336);
  context.textAlign = 'left';

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (result) => {
        if (result) resolve(result);
        else reject(new Error('The report image could not be created.'));
      },
      'image/png',
      0.96,
    );
  });

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `my-${analysis.primary.id}-face-shape-report.png`;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
